import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useTrainingLesson, { useTrainingLessonForm } from '@hooks/useTrainingLesson'

import TrainingLessonAliasSelector from '@components/TrainingLessonAliasSelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { TrainingLessonModel } from '@models/trainingLesson'

const MODAL_KEY = 'CreateTrainingLessonModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTrainingLesson: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateTrainingLessonModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createTrainingLesson } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { trainingLesson: initTrainingLesson } = modalPayload

  const {
    callbacks: { createTrainingLesson: createFn },
    creating,
    trainingLesson,
  } = useTrainingLesson(initTrainingLesson)

  const {
    entityState,
    entityState: {
      description,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useTrainingLessonForm(trainingLesson, { validateOn: 'change' })

  const createTrainingLessonPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_training_lesson_training_module: true,
      include_training_module_lesson_count: true,
    },
    shouldRedirect: true,
    toastText: 'Training Lesson Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Training Lesson" />

      <SidebarModal.Content>
        <Form>
          <TrainingLessonAliasSelector
            callbacks={{
              selectTrainingLesson: (lesson: TrainingLessonModel) => {
                setEntityState({
                  description: lesson?.description,
                  lesson_alias_id: lesson?.id,
                  title: lesson?.title,
                })
              },
            }}
          />

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Description" errorMessage={errors.description} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="description"
              style={{ height: 120, resize: 'vertical' }}
              value={description}
              {...handlers}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createTrainingLesson(createTrainingLessonPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Training Lesson' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateTrainingLessonModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
